import React from "react";

import { navigate } from "@reach/router";
import { connect, ConnectedProps } from "react-redux";

import { CartReduxState } from "cuanto/features/Cart";
import { NotFound } from "cuanto/web-app/Screens";

import useTracker from "../../hooks/useTracker";
import LoadingView from "../components/LoadingView";
import useCatalogue from "../contexts/CatalogueContext";
import useCatalogueProduct from "../hooks/useCatalogueProduct";
import { Product } from "../types";
import CatalogueLink from "./CatalogueLink";

type RouteProps = { username: string; short_uuid: string; tracker?: string };

function CatalogueLinkScreen({
  username,
  short_uuid,
  tracker,
  ...props
}: ReduxProps & RouteProps) {
  useTracker(tracker);

  const { addToCart } = useCatalogue();

  const { isLoading, product, productVariants, prices, merchant, storeClosed } =
    useCatalogueProduct(username, short_uuid);

  function onGoToCart() {
    navigate(`/${username}/cart`, { state: { merchant } });
  }

  function onAddToCart(product: Product) {
    const { redirectTo } = addToCart(product);
    const shortUuid = (product.product_group_uuid || product.uuid).slice(-6);
    switch (redirectTo) {
      case "prices":
        navigate(`/${username}/p/${shortUuid}/prices`);
        break;
      case "cart":
        navigate(`/${username}/cart`);
        break;
      case "catalogue":
        navigate(`/${username}`);
        break;
    }
  }

  function onAskQuestions() {
    navigate(`/${username}/p/${short_uuid}/questions`);
  }

  function onBackPress() {
    // if there is state history, means we navigated from cuanto
    if (window.history.state) {
      window.history.back();
    } else {
      // no cuanto history to go back (from mercadito, or shared product link, etc...)
      navigate(`/${username}`);
    }
  }

  if (isLoading) return <LoadingView />;

  if (!product) return <NotFound />;

  return (
    <CatalogueLink
      merchant={merchant}
      prices={prices}
      product={product}
      productVariants={productVariants}
      onAskQuestions={onAskQuestions}
      cartQuantity={props.quantity}
      storeClosed={storeClosed}
      onAddToCart={onAddToCart}
      onGoToCart={onGoToCart}
      onBackPress={onBackPress}
    />
  );
}

const mapStateToProps = ({ cart }: { cart: CartReduxState }) => ({
  quantity: cart.quantity,
});

const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CatalogueLinkScreen);
